export type EasingTypes =
  | "linear"
  | "easeInQuad"
  | "easeOutQuad"
  | "easeInOutQuad"
  | "easeInCubic"
  | "easeOutCubic"
  | "easeInOutCubic"
  | "easeInQuart"
  | "easeOutQuart"
  | "easeInOutQuart"
  | "easeInQuint"
  | "easeOutQuint"
  | "easeInOutQuint";

const easingFunctions: { [s: string]: (t: number) => number } = {
  linear(t: number): number {
    return t;
  },
  easeInQuad(t: number): number {
    return t * t;
  },
  easeOutQuad(t: number): number {
    return t * (2 - t);
  },
  easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  easeInCubic(t: number): number {
    return t * t * t;
  },
  easeOutCubic(t: number): number {
    return --t * t * t + 1;
  },
  easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  easeInQuart(t: number): number {
    return t * t * t * t;
  },
  easeOutQuart(t: number): number {
    return 1 - --t * t * t * t;
  },
  easeInOutQuart(t: number): number {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  },
  easeInQuint(t: number): number {
    return t * t * t * t * t;
  },
  easeOutQuint(t: number): number {
    return 1 + --t * t * t * t * t;
  },
  easeInOutQuint(t: number): number {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  },
};

function isValidValue(value: number): boolean {
  if (value === undefined || isNaN(value) || value === null) return false;
  return true;
}

function isDestinationValid(destination: number | HTMLElement): boolean {
  if (!destination) {
    return false;
  }
  if (typeof destination === "number") {
    const val = isValidValue(destination);
    return val;
  }
  return true;
}

export function scrollTo(destination: number | HTMLElement, duration = 200, easing: EasingTypes = "linear"): void {
  if (!isDestinationValid(destination)) {
    return;
  }

  const start = window.pageYOffset;
  const startTime = "now" in window.performance ? performance.now() : new Date().getTime();

  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
  );
  const windowHeight =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.getElementsByTagName("body")[0].clientHeight;

  const destinationOffset = typeof destination === "number" ? destination : destination.offsetTop;

  const destinationOffsetToScroll = Math.round(
    documentHeight - destinationOffset < windowHeight ? documentHeight - windowHeight : destinationOffset,
  );

  if (!isValidValue(destinationOffsetToScroll)) {
    return;
  }

  function scroll(): void {
    const now = "now" in window.performance ? performance.now() : new Date().getTime();
    const time = Math.min(1, (now - startTime) / duration);
    const timeFunction = easingFunctions[easing](time);

    const scrollDist = Math.ceil(timeFunction * (destinationOffsetToScroll - start) + start);
    window.scroll(0, scrollDist);

    if (!isValidValue(destinationOffsetToScroll) || !isValidValue(window.pageYOffset)) {
      return;
    }

    if (Math.abs(window.pageYOffset - destinationOffsetToScroll) <= 5 || destinationOffsetToScroll <= 0) {
      return;
    }

    requestAnimationFrame(scroll);
  }

  scroll();
}
